import { FeatureFlag, useFeatureFlag, useFeatureFlagsReadyFor } from 'utils/featureFlags'

import { WorkspaceSeatType } from 'models/user'
import { useAuth0 } from 'utils/auth'
import { useClientProperty } from '../_main/contexts'
import { useUserData } from 'components/contexts/UserDataContext'

export interface MarketingCampaignAccess {
  /** False while what decides is still loading: wait, a redirect now would bounce a user who gets the tab */
  decided: boolean
  /** The tab shows and its route renders; false until decided */
  allowed: boolean
}

const UNDECIDED: MarketingCampaignAccess = { decided: false, allowed: false }
const DENIED: MarketingCampaignAccess = { decided: true, allowed: false }
const ALLOWED: MarketingCampaignAccess = { decided: true, allowed: true }

/** The seats `/social-campaign/**` lets through (`minSeatType = SPONSORED`); a RESTRICTED one is refused on every call. */
const CAMPAIGN_SEATS: ReadonlySet<WorkspaceSeatType> = new Set([WorkspaceSeatType.SPONSORED, WorkspaceSeatType.FULL])

/**
 * Who gets the property's Marketing campaign tab (epic ISSUE-3615): a client, the backend's controllers
 * being `role = CLIENT` only, with `marketing-automation` on, holding a SPONSORED or FULL seat in the
 * property's workspace, the one the campaign endpoints use (`resolvedWorkspaceId`), not the selected one
 * the page switches to only later. Fails closed: no membership there or an unknown seat is no tab. The
 * tab bar and the route read the same answer.
 */
export const useMarketingCampaignAccess = (): MarketingCampaignAccess => {
  const marketingAutomation = useFeatureFlag(FeatureFlag.MARKETING_AUTOMATION)
  const { isAuthenticated, roles, user } = useAuth0()
  // A per-user rollout reads false on the anonymous context until identify settles (App.controller's identity)
  const flagsReady = useFeatureFlagsReadyFor(isAuthenticated ? user?.email : undefined)
  const { currentUserWorkspace, getWorkspaceItemById, isWorkspaceResolved } = useUserData()
  const { resolvedWorkspaceId, isPropertyWorkspaceResolved } = useClientProperty()

  // On a reload `isAuthenticated` lands a render before the Auth0 user its roles are read from
  if (!roles.isClient) return user ? DENIED : UNDECIDED
  if (!flagsReady) return UNDECIDED
  if (!marketingAutomation) return DENIED
  if (!isWorkspaceResolved || !isPropertyWorkspaceResolved) return UNDECIDED

  // The selection when the property lives in it: a pinned embed workspace is outside the lookup
  const membership = resolvedWorkspaceId && resolvedWorkspaceId === currentUserWorkspace?.id
    ? currentUserWorkspace
    : getWorkspaceItemById(resolvedWorkspaceId)
  return membership && CAMPAIGN_SEATS.has(membership.seatType) ? ALLOWED : DENIED
}
